import { useState } from "react";
import axios from "axios";
import { X, Plus, Loader2, Save, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls = "h-10 border-white/15 bg-white/5 text-sm text-white focus-visible:ring-[#FFE500]/50";
const labelCls = "text-xs text-white/60";
const DESIGNATIONS = [
  "Pastor", "Evangelist", "Missionary", "Church Leader",
  "Student (Undergraduate / Postgraduate)", "Business Professional", "Employee", "Member", "Other",
];

const Sel = ({ value, onChange, options, testId }) => (
  <select data-testid={testId} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
    className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-2 text-sm text-white [color-scheme:dark]">
    {options.map((o) => (
      <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
    ))}
  </select>
);

const F = ({ label, children, testId }) => (
  <div data-testid={testId}>
    <Label className={labelCls}>{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

const YES_NO_OPTS = [{ value: "yes", label: "Yes" }, { value: "no", label: "No" }];

export const EditRegistration = ({ reg, token, onClose, onSaved }) => {
  const [draft, setDraft] = useState(() => ({
    personal: { ...reg.personal },
    church: { ...reg.church },
    family: {
      attending_as_family: reg.family?.attending_as_family || "no",
      members: (reg.family?.members || []).map((m) => ({ ...m })),
    },
    travel: { ...reg.travel },
    payment_verified: !!reg.payment_verified,
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const setP = (k, v) => setDraft((d) => ({ ...d, personal: { ...d.personal, [k]: v } }));
  const setC = (k, v) => setDraft((d) => ({ ...d, church: { ...d.church, [k]: v } }));
  const setT = (k, v) => setDraft((d) => ({ ...d, travel: { ...d.travel, [k]: v } }));
  const setMember = (i, k, v) =>
    setDraft((d) => {
      const members = [...d.family.members];
      members[i] = { ...members[i], [k]: v };
      return { ...d, family: { ...d.family, members } };
    });

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await axios.patch(
        `${API}/admin/registrations/${reg.registration_id}`,
        {
          payment_verified: draft.payment_verified,
          personal: draft.personal,
          church: draft.church,
          family: draft.family,
          travel: draft.travel,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSaved(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Save failed — please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="edit-modal" className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm sm:items-center">
      <div className="my-8 w-full max-w-3xl rounded-2xl border border-white/15 bg-[#111B45] p-6 shadow-2xl sm:p-8">
        <div className="flex items-center justify-between">
          <div>
            <h2 data-testid="edit-modal-title" className="font-display text-xl font-bold uppercase tracking-wide text-white">
              Edit Registration
            </h2>
            <p className="mt-0.5 text-sm font-semibold text-[#FFE500]">{reg.registration_id}</p>
          </div>
          <button data-testid="edit-close-btn" onClick={onClose} aria-label="Close"
            className="rounded-full border border-white/15 p-2 text-white/60 hover:border-[#FFE500]/50 hover:text-[#FFE500]">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-6 space-y-6">
          {/* Personal */}
          <section>
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-[#FFE500]">Personal</p>
            <div className="grid gap-3 sm:grid-cols-3">
              <F label="Full Name" testId="edit-f-name"><Input data-testid="edit-full-name" className={inputCls} value={draft.personal.full_name || ""} onChange={(e) => setP("full_name", e.target.value)} /></F>
              <F label="Gender" testId="edit-f-gender"><Sel testId="edit-gender" value={draft.personal.gender} onChange={(v) => setP("gender", v)} options={[{ value: "male", label: "Male" }, { value: "female", label: "Female" }]} /></F>
              <F label="Age" testId="edit-f-age"><Input data-testid="edit-age" type="number" className={inputCls} value={draft.personal.age ?? ""} onChange={(e) => setP("age", e.target.value)} /></F>
              <F label="Date of Birth" testId="edit-f-dob"><Input data-testid="edit-dob" className={inputCls} value={draft.personal.dob || ""} onChange={(e) => setP("dob", e.target.value)} placeholder="DD/MM/YYYY" /></F>
              <F label="Mobile" testId="edit-f-mobile"><Input data-testid="edit-mobile" className={inputCls} value={draft.personal.mobile || ""} onChange={(e) => setP("mobile", e.target.value)} /></F>
              <F label="Email" testId="edit-f-email"><Input data-testid="edit-email" type="email" className={inputCls} value={draft.personal.email || ""} onChange={(e) => setP("email", e.target.value)} /></F>
              <F label="Address" testId="edit-f-address"><Input data-testid="edit-address" className={inputCls} value={draft.personal.address || ""} onChange={(e) => setP("address", e.target.value)} /></F>
              <F label="State" testId="edit-f-state"><Input data-testid="edit-state" className={inputCls} value={draft.personal.state || ""} onChange={(e) => setP("state", e.target.value)} /></F>
              <F label="Country" testId="edit-f-country"><Input data-testid="edit-country" className={inputCls} value={draft.personal.country || ""} onChange={(e) => setP("country", e.target.value)} /></F>
            </div>
          </section>

          {/* Church */}
          <section>
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-[#FFE500]">Church & Ministry</p>
            <div className="grid gap-3 sm:grid-cols-3">
              <F label="Church / Organization" testId="edit-f-church"><Input data-testid="edit-church" className={inputCls} value={draft.church.church_name || ""} onChange={(e) => setC("church_name", e.target.value)} /></F>
              <F label="Designation" testId="edit-f-designation"><Sel testId="edit-designation" value={draft.church.designation} onChange={(v) => setC("designation", v)} options={DESIGNATIONS} /></F>
              {draft.church.designation === "Other" && (
                <F label="Specify" testId="edit-f-designation-other"><Input data-testid="edit-designation-other" className={inputCls} value={draft.church.designation_other || ""} onChange={(e) => setC("designation_other", e.target.value)} /></F>
              )}
              <F label="Reference / Heard via" testId="edit-f-reference"><Input data-testid="edit-reference" className={inputCls} value={draft.church.reference || ""} onChange={(e) => setC("reference", e.target.value)} /></F>
            </div>
          </section>

          {/* Family */}
          <section>
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-[#FFE500]">Family</p>
            <div className="grid gap-3 sm:grid-cols-3">
              <F label="Attending as family" testId="edit-f-family"><Sel testId="edit-family" value={draft.family.attending_as_family} onChange={(v) => setDraft((d) => ({ ...d, family: { ...d.family, attending_as_family: v } }))} options={YES_NO_OPTS} /></F>
            </div>
            <div className="mt-3 space-y-2">
              {draft.family.members.map((m, i) => (
                <div key={i} data-testid={`edit-member-${i}`} className="flex flex-wrap items-end gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-3">
                  <div className="w-28">
                    <Label className={labelCls}>Relation</Label>
                    <Sel testId={`edit-member-relation-${i}`} value={m.relation} onChange={(v) => setMember(i, "relation", v)}
                      options={[{ value: "spouse", label: "Spouse" }, { value: "child", label: "Child" }, { value: "parent", label: "Parent" }]} />
                  </div>
                  <div className="min-w-[120px] flex-1">
                    <Label className={labelCls}>Name</Label>
                    <Input data-testid={`edit-member-name-${i}`} className={`${inputCls} mt-1`} value={m.name} onChange={(e) => setMember(i, "name", e.target.value)} />
                  </div>
                  <div className="w-20">
                    <Label className={labelCls}>Age</Label>
                    <Input data-testid={`edit-member-age-${i}`} type="number" className={`${inputCls} mt-1`} value={m.age} onChange={(e) => setMember(i, "age", e.target.value)} />
                  </div>
                  <div className="w-28">
                    <Label className={labelCls}>Gender</Label>
                    <Sel testId={`edit-member-gender-${i}`} value={m.gender} onChange={(v) => setMember(i, "gender", v)}
                      options={[{ value: "male", label: "Male" }, { value: "female", label: "Female" }]} />
                  </div>
                  <button type="button" data-testid={`edit-member-remove-${i}`} aria-label="Remove member"
                    onClick={() => setDraft((d) => ({ ...d, family: { ...d.family, members: d.family.members.filter((_, idx) => idx !== i) } }))}
                    className="flex h-10 w-9 items-center justify-center rounded-md border border-white/15 text-white/50 hover:border-red-400 hover:text-red-400">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              <button type="button" data-testid="edit-add-member-btn"
                onClick={() => setDraft((d) => ({ ...d, family: { ...d.family, members: [...d.family.members, { relation: "child", name: "", age: "", gender: "" }] } }))}
                className="flex items-center gap-2 rounded-full border border-[#FFE500]/40 px-4 py-2 text-xs font-bold uppercase tracking-wider text-[#FFE500] hover:bg-[#FFE500] hover:text-[#0B1233]">
                <Plus className="h-3.5 w-3.5" /> Add family member
              </button>
            </div>
          </section>

          {/* Travel */}
          <section>
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-[#FFE500]">Travel & Accommodation</p>
            <div className="grid gap-3 sm:grid-cols-3">
              <F label="From outside Bangalore" testId="edit-f-outside"><Sel testId="edit-outside" value={draft.travel.from_outside} onChange={(v) => setT("from_outside", v)} options={YES_NO_OPTS} /></F>
              <F label="Needs accommodation" testId="edit-f-accom"><Sel testId="edit-accom" value={draft.travel.needs_accommodation} onChange={(v) => setT("needs_accommodation", v)} options={YES_NO_OPTS} /></F>
              <F label="Accommodation option" testId="edit-f-accom-type">
                <Sel testId="edit-accom-type" value={draft.travel.accommodation_type || ""} onChange={(v) => setT("accommodation_type", v)}
                  options={[{ value: "", label: "—" }, { value: "dormitory", label: "In-Campus Dormitory" }, { value: "hotel", label: "Outside Hotel / Airbnb" }]} />
              </F>
              <F label="Arrival" testId="edit-f-arrival"><Input data-testid="edit-arrival" type="datetime-local" className={`${inputCls} [color-scheme:dark]`} value={draft.travel.arrival || ""} onChange={(e) => setT("arrival", e.target.value)} /></F>
              <F label="Departure" testId="edit-f-departure"><Input data-testid="edit-departure" type="datetime-local" className={`${inputCls} [color-scheme:dark]`} value={draft.travel.departure || ""} onChange={(e) => setT("departure", e.target.value)} /></F>
            </div>
          </section>

          {/* Payment */}
          <section className="rounded-xl border border-[#FFE500]/25 bg-[#FFE500]/[0.05] p-4">
            <label className="flex cursor-pointer items-center gap-3">
              <Checkbox data-testid="edit-verified-checkbox" checked={draft.payment_verified}
                onCheckedChange={(v) => setDraft((d) => ({ ...d, payment_verified: v === true }))}
                className="h-5 w-5 border-white/40 data-[state=checked]:border-[#FFE500] data-[state=checked]:bg-[#FFE500] data-[state=checked]:text-[#0B1233]" />
              <span className="text-sm font-semibold text-white">Payment verified</span>
            </label>
          </section>

          {error && <p data-testid="edit-error" className="rounded-xl border border-red-400/50 bg-red-400/10 p-3 text-sm text-red-300">{error}</p>}

          <div className="flex justify-end gap-3">
            <Button data-testid="edit-cancel-btn" variant="outline" onClick={onClose}
              className="rounded-full border-white/20 bg-transparent text-white hover:border-[#FFE500]/50 hover:text-[#FFE500]">
              Cancel
            </Button>
            <Button data-testid="edit-save-btn" onClick={save} disabled={saving}
              className="rounded-full bg-[#FFE500] font-display font-bold uppercase tracking-wider text-[#0B1233] hover:bg-[#FFE500]/90">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" /> Save Changes</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
